/**
 * This component is a resolver. It is meant to be used by page templates using
 * data from a CMS.
 *
 * You can send the data you receive from the CMS to the props of this components
 * by spreading them (like <Teasers {...cmsData} />)
 * and then make these props fit the final module.
 */
import React from "react"
import { Section, CmsImage } from "@bw/bits"
import { Teasers as TeasersModule } from "@bw/modules"
import { Teaser } from "@bw/partials"
import { Link, useStaticQuery, graphql } from "gatsby"
import dateFormat from "utils/dateFormat"
import shuffle from "utils/shuffle"

const TeasersLatest = ({
  suptitle,
  title,
  subtitle,
  button,
  buttonPosition,
  postType,
  maxItems,
  numberOfColumns,
  composition,
  exclude,
  anchor,
}) => {
  const data = useStaticQuery(graphql`
    query LatestItemsInCollection {
      pages: allCollectionPages(sort: { date: DESC }) {
        nodes {
          id
          title
          slug
          teaserImage {
            alt
            has_focus
            focus_css
            localImage {
              childImageSharp {
                gatsbyImageData(width: 350)
              }
            }
          }
          internal {
            type
          }
        }
      }
      articles: allCollectionArticles(sort: { date: DESC }) {
        nodes {
          id
          title
          slug
          teaserImage {
            alt
            has_focus
            focus_css
            localImage {
              childImageSharp {
                gatsbyImageData(width: 350)
              }
            }
          }
          internal {
            type
          }
        }
      }
      news: allCollectionNews(sort: { date: DESC }) {
        nodes {
          id
          title
          slug
          date
          teaserImage {
            alt
            has_focus
            focus_css
            localImage {
              childImageSharp {
                gatsbyImageData(width: 350)
              }
            }
          }
          internal {
            type
          }
        }
      }
      magazines: allCollectionMagazines(sort: { publishDate: DESC }) {
        nodes {
          id
          title
          slug
          teaserImage: cover {
            alt
            has_focus
            focus_css
            localImage {
              childImageSharp {
                gatsbyImageData(width: 300)
              }
            }
          }
          internal {
            type
          }
        }
      }
      jobs: allCollectionJobs(sort: { date: DESC }) {
        nodes {
          id
          title
          slug
          internal {
            type
          }
        }
      }
    }
  `)

  let items = postType.reduce((acc, type) => {
    acc = [...acc, ...data[type.value].nodes]
    return acc
  }, [])

  if (exclude && exclude.length) {
    items = items.filter(item => !exclude.includes(item.id))
  }

  if (maxItems) {
    items = items.slice(0, maxItems)
  }

  if (composition?.value === "random") {
    items = shuffle(items)
  }

  return (
    <Section
      {...{
        suptitle,
        title,
        subtitle,
        button,
        buttonPosition: buttonPosition?.value,
        anchor,
      }}
    >
      <TeasersModule numberOfColumns={numberOfColumns?.value}>
        {items.map((item, i) => {
          if (item.internal.type === "CollectionMagazines") {
            return (
              <Link key={i} to={item.slug}>
                {item.teaserImage?.localImage && (
                  <CmsImage
                    src={item.teaserImage || "/placeholder-teaser.jpg"}
                    alt={item.title}
                  />
                )}
              </Link>
            )
          }
          return (
            <Teaser
              key={i}
              suptitle={item?.date ? dateFormat(item.date) : undefined}
              title={item.title}
              image={
                <CmsImage
                  src={item.teaserImage || "/placeholder-teaser.jpg"}
                  alt={item.title}
                />
              }
              to={item.slug}
            />
          )
        })}
      </TeasersModule>
    </Section>
  )
}

/**
 * This generates a GraphQL Fragment that can be used in a page query:
 *
 * Example:
 * ... on TeasersLatestModule {
 *   ...TeasersLatestModuleFragment
 * }
 *
 * This is also what you will get in the props of your {{pascalCase name}}
 * resolver component.
 */
export const query = graphql`
  fragment TeasersLatestModuleFragment on TeasersLatestModule {
    composition {
      value
    }
    maxItems
    postType {
      value
    }
    numberOfColumns {
      value
    }
  }
`

export { TeasersLatest }
